import React, { useEffect, useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { VideoHighlight, videoHighlightsService } from '../../../shared/services/videoHighlightsService';
import { scoresheetService } from '../../../shared/services/scoresheetService';
import { useAuth } from '../../../shared/contexts/AuthContext';

type MatchPlayer = { player_id: string; full_name: string; kit_number: number | null; team_id: string; team_name: string };

interface EditHighlightModalProps {
  isOpen: boolean;
  highlight: VideoHighlight | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (updates: { name: string; player_ids?: string[] | null }) => void;
}

const EditHighlightModal: React.FC<EditHighlightModalProps> = ({
  isOpen,
  highlight,
  isSaving,
  onClose,
  onSave,
}) => {
  const { user } = useAuth();
  const [name, setName] = useState(highlight?.name || '');
  const [isActive, setIsActive] = useState(true);
  const [isUpdatingActive, setIsUpdatingActive] = useState(false);
  const [matchPlayers, setMatchPlayers] = useState<MatchPlayer[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  useEffect(() => {
    if (isOpen && highlight) {
      setName(highlight.name);
      setIsActive(highlight.is_active);
      setSelectedPlayerIds(Array.isArray(highlight.player_ids) ? highlight.player_ids : []);
      setMatchPlayers([]);
      setLoadingPlayers(true);
      scoresheetService.getMatchLineupPlayers(highlight.match_id).then((players) => {
        setMatchPlayers(players);
      }).catch(() => {
        setMatchPlayers([]);
      }).finally(() => {
        setLoadingPlayers(false);
      });
    }
  }, [isOpen, highlight]);

  if (!highlight) return null;

  // Check if highlight has any storage files
  const hasStorageFiles = Boolean(
    highlight.output_storage_path ||
    highlight.enhanced_storage_path ||
    highlight.output_url ||
    highlight.enhanced_url
  );

  // Disable is_active toggle if files exist
  const canDeactivate = !hasStorageFiles;

  const togglePlayer = (playerId: string) => {
    setSelectedPlayerIds((prev) =>
      prev.includes(playerId) ? prev.filter((id) => id !== playerId) : [...prev, playerId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !user?.id) return;
    
    await onSave({ name: name.trim(), player_ids: selectedPlayerIds.length > 0 ? [...new Set(selectedPlayerIds)] : null });
    
    // Update is_active separately if it changed
    if (highlight.is_active !== isActive && canDeactivate) {
      setIsUpdatingActive(true);
      try {
        const result = await videoHighlightsService.updateHighlightIsActive(highlight.id, isActive, user.id);
        if (!result.success) {
          console.error('Failed to update is_active:', result.error);
          // Revert the toggle on error
          setIsActive(highlight.is_active);
        }
      } catch (err) {
        console.error('Failed to update is_active:', err);
        // Revert the toggle on error
        setIsActive(highlight.is_active);
      } finally {
        setIsUpdatingActive(false);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Highlight" className="max-w-md">
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div>
          <label htmlFor="edit-highlight-name" className="block text-sm font-medium text-gray-700 mb-2">
            Highlight name
          </label>
          <input
            id="edit-highlight-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter highlight name"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={80}
            autoFocus
          />
          <p className="text-xs text-gray-400 mt-1">
            Rename this highlight. This does not affect the saved clips or processing status.
          </p>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Associated players (optional)</label>
          <p className="text-xs text-gray-500 mb-3">
            Link this highlight to one or more players. Leave unchecked for a team-level highlight.
          </p>
          {loadingPlayers ? (
            <p className="text-sm text-gray-500">Loading players…</p>
          ) : matchPlayers.length === 0 ? (
            <p className="text-sm text-gray-500">No lineup players for this match.</p>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-2 rounded border border-gray-200 p-2">
              {matchPlayers.map((p) => (
                <label key={`${p.player_id}-${p.team_id}`} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={selectedPlayerIds.includes(p.player_id)}
                    onChange={() => togglePlayer(p.player_id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-900">
                    {p.full_name}
                    {p.kit_number != null && <span className="text-gray-500"> (#{p.kit_number})</span>}
                    <span className="text-gray-400 text-xs ml-1">— {p.team_name}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <p className="text-xs text-gray-500">
              {canDeactivate
                ? 'Inactive highlights won\'t appear in the list'
                : 'Cannot deactivate highlight with existing files. Delete files first.'}
            </p>
          </div>
            <label className={`flex items-center ${canDeactivate ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => {
                  if (canDeactivate) {
                    setIsActive(e.target.checked);
                  }
                }}
                disabled={!canDeactivate || isUpdatingActive}
                className="sr-only"
              />
              <div
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isActive ? 'bg-blue-600' : 'bg-gray-300'
                } ${canDeactivate ? '' : 'opacity-50'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </label>
        </div>

        <div className="flex justify-end space-x-3 border-t border-gray-100 pt-4">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSaving || isUpdatingActive}>
            Close
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving || isUpdatingActive || !name.trim()}>
            {isSaving || isUpdatingActive ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditHighlightModal;


